import { useCallback, useState } from 'react';
import { createReport, getReport, getReportDownloadUrl } from '../lib/api/endpoints';

const ESPERAS_MS = [0, 1500, 3000, 5000, 5000, 8000, 8000];
const dormir = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * FE-32 — exportar el reporte FINANCIAL (ADR-030 §6): `POST /reports`, polling
 * del estado y descarga por URL firmada. El archivo lo arma el core desde los
 * agregados: por eso sus totales cuadran con la página Finanzas y con el
 * cuaderno, y por eso el CSV de cliente se retiró (#157).
 *
 * `from`/`to` son fechas `YYYY-MM-DD` del día local; se traducen al rango
 * ISO que el contrato pide (inicio del primer día, fin del último).
 */
export function useFinancialReport() {
  const [exportando, setExportando] = useState<'XLSX' | 'CSV' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const exportar = useCallback(async (formato: 'XLSX' | 'CSV', desde: string, hasta: string, subunitId?: string) => {
    setExportando(formato);
    setError(null);
    try {
      const inicio = new Date(`${desde}T00:00:00`);
      const fin = new Date(`${hasta}T23:59:59.999`);
      let reporte = await createReport({ type: 'FINANCIAL', format: formato, from: inicio.toISOString(), to: fin.toISOString(), subunitId: subunitId ?? null });
      for (let intento = 0; reporte.status === 'GENERATING' && intento < ESPERAS_MS.length; intento++) {
        await dormir(ESPERAS_MS[intento]);
        reporte = await getReport(reporte.id);
      }
      if (reporte.status === 'FAILED') throw new Error('El servidor no pudo generar el reporte. Inténtalo de nuevo.');
      if (reporte.status !== 'READY') throw new Error('El reporte sigue generándose. Búscalo en Reportes en unos minutos.');
      const { url } = await getReportDownloadUrl(reporte.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const status = (err as { status?: number })?.status;
      const detail = (err as { detail?: string })?.detail;
      if (status === 501) setError('La exportación no está configurada en este entorno (falta el bucket de reportes).');
      else if (status === 403) setError('Tu rol no puede pedir este reporte.');
      else setError(detail ?? (err instanceof Error ? err.message : 'No se pudo exportar.'));
    } finally {
      setExportando(null);
    }
  }, []);

  return { exportar, exportando, error, limpiarError: () => setError(null) };
}
